import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { BchnostrVerifyDialog } from '@/components/BchnostrVerifyDialog';
import {
  MapPin,
  ShieldCheck,
  ArrowLeft,
  CheckCircle2,
  MessageCircle,
  Coins,
  Clock,
  Star,
  TrendingUp,
  Eye,
  Store,
  ExternalLink,
} from 'lucide-react';

const ADMIN_NPUB = 'npub1madu4w57wnxpwexfwuawzcpfnh094nmeg9hze9n43kazyhn8qlxq4lrgfg';
const ADMIN_HEX = 'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc';
const NOSTR_PROFILE_URL = `https://bchnostr.com/profile/${ADMIN_HEX}`;

export default function GetVerified() {
  useSeoMeta({
    title: 'Get Verified — BCH Local',
    description: 'Get your business verified on the BCH Local. Stand out from the crowd with a verified badge that shows customers you\'re a trusted Bitcoin Cash merchant.',
  });

  const [verifyOpen, setVerifyOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container flex items-center justify-between h-14 px-4 mx-auto max-w-3xl">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="flex items-center justify-center size-8 rounded-lg bg-bch">
              <MapPin className="size-4 text-bch-foreground" />
            </div>
            <span className="font-bold text-base sm:text-lg">BCH Local</span>
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="container px-4 mx-auto max-w-3xl py-6 space-y-8">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="size-4" />
          Back to Map
        </Link>

        {/* Hero */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center size-16 rounded-2xl bg-bch/10 mx-auto">
            <ShieldCheck className="size-8 text-bch" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Get <span className="text-bch">Verified</span>
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-base sm:text-lg">
            Stand out on the BCH Local with a verified badge that tells customers your business is real and trusted.
          </p>
        </div>

        {/* Self-service BCHNostr verification */}
        <Card className="border-bch/30 bg-bch/5">
          <CardContent className="py-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center size-10 rounded-lg bg-bch/10 shrink-0">
                <ShieldCheck className="size-5 text-bch" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold">Listed on BCHNostr? Verify yourself now</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  If your business is published on BCHNostr, you can get verified instantly.
                  Pay the one-time $10 fee in BCH, submit your transaction, and an issuer grants
                  your badge — valid for a full year.
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground">
              <strong className="text-amber-700 dark:text-amber-400">Important:</strong> log in
              with your <strong className="text-foreground">business npub</strong> (the account
              that published your listing), not your personal account.
            </div>
            <Button
              className="w-full sm:w-auto bg-bch hover:bg-bch/90 text-bch-foreground"
              onClick={() => setVerifyOpen(true)}
            >
              <ShieldCheck className="size-4 mr-2" />
              Get Verified on BCHNostr
            </Button>
          </CardContent>
        </Card>

        {/* What is verification */}
        <Card>
          <CardContent className="py-6 space-y-4">
            <h2 className="text-lg font-semibold">What does verified mean?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The verified badge is a green shield (<ShieldCheck className="size-4 text-bch inline -mt-0.5" />) displayed next to your business name across the entire BCH Local. It signals to customers that your listing has been manually reviewed and confirmed as a legitimate business accepting Bitcoin Cash.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Verification is <strong className="text-foreground">not</strong> an endorsement or guarantee of service quality &mdash; it simply means we&apos;ve confirmed that the business exists, accepts BCH, and the listing information is accurate.
            </p>
          </CardContent>
        </Card>

        {/* Benefits */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Why get verified?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { icon: ShieldCheck, title: 'Trust signal', desc: 'Customers prefer verified merchants over unverified ones' },
              { icon: Eye, title: 'Stand out', desc: 'Your badge is visible on the map, list view, and your merchant page' },
              { icon: TrendingUp, title: 'Higher ranking', desc: 'Verified merchants appear above unverified ones in filtered results' },
              { icon: Star, title: 'Spam protection', desc: 'Distinguishes your real business from fake or outdated listings' },
            ].map(({ icon: Icon, title, desc }) => (
              <Card key={title}>
                <CardContent className="py-4 px-4 flex items-start gap-3">
                  <div className="flex items-center justify-center size-9 rounded-lg bg-bch/10 shrink-0">
                    <Icon className="size-4 text-bch" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{title}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Separator />

        {/* How it works */}
        <div>
          <h2 className="text-lg font-semibold mb-4">How to get verified</h2>
          <div className="space-y-4">
            {[
              {
                step: 1,
                icon: Store,
                title: 'Publish your business on BCHNostr',
                desc: 'Your business needs a listing on BCHNostr first. Create a business page there — add your name, category, and location — and it appears on the BCH Local map automatically.',
                cta: { label: 'Add your business on BCHNostr', href: 'https://bchnostr.com/merchants' },
              },
              {
                step: 2,
                icon: Coins,
                title: 'Pay the one-time $10 fee in BCH',
                desc: 'Open the verification dialog, scan the QR code or copy the payment link, and pay the one-time $10 USD fee (in Bitcoin Cash) from your wallet. No recurring charges.',
                cta: null,
              },
              {
                step: 3,
                icon: CheckCircle2,
                title: 'Submit your transaction',
                desc: 'Paste your payment transaction ID (txid) back into the dialog. We confirm the payment on-chain and publish a signed claim from your business account.',
                cta: null,
              },
              {
                step: 4,
                icon: ShieldCheck,
                title: 'Get your verified badge',
                desc: 'Once an issuer reviews your claim, your green verified badge appears across the map — valid for a full year.',
                cta: null,
              },
            ].map(({ step, icon: Icon, title, desc, cta }) => (
              <div key={step} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center size-10 rounded-full bg-bch text-bch-foreground font-bold text-sm shrink-0">
                    {step}
                  </div>
                  {step < 4 && <div className="w-px flex-1 bg-border mt-2" />}
                </div>
                <div className="pb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="size-4 text-muted-foreground" />
                    <h3 className="font-medium text-sm">{title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  {cta && (
                    <a
                      href={cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-bch hover:text-bch/80 transition-colors"
                    >
                      <ExternalLink className="size-3.5" />
                      {cta.label}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* FAQ */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Frequently asked questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'How much does verification cost?',
                a: 'A one-time payment of $10 USD, paid in Bitcoin Cash. No recurring fees.',
              },
              {
                q: 'How long does it take?',
                a: 'Your payment is confirmed on-chain right away. An issuer then reviews your claim and grants the badge — usually within 24 hours.',
              },
              {
                q: 'Which account should I use?',
                a: 'Log in with your business npub — the same Nostr account that published your business on BCHNostr. Do not use your personal account, or the badge will attach to the wrong profile.',
              },
              {
                q: 'Can my verification be revoked?',
                a: 'Yes. If your business closes, stops accepting BCH, or your listing is found to contain inaccurate information, the verified badge may be removed.',
              },
              {
                q: 'I don\'t have a listing yet. How do I get listed?',
                a: 'Publish a business page on BCHNostr. Add your business name, category, and location, and your listing appears on the BCH Local map automatically.',
              },
              {
                q: 'Do I need to renew verification?',
                a: 'Verification lasts one year. When it nears expiry, simply pay the fee again and submit a new claim to renew your badge.',
              },
            ].map(({ q, a }) => (
              <div key={q}>
                <h3 className="text-sm font-medium mb-1">{q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Sponsored Listings */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center size-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 mx-auto">
            <Star className="size-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Promote Your <span className="text-amber-600 dark:text-amber-400">Listing</span>
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            Want even more visibility? Sponsored merchants get premium placement at the top of all search results and a highlighted card that catches the eye.
          </p>
        </div>

        <Card className="border-amber-200 dark:border-amber-800/40">
          <CardContent className="py-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Sponsored Listing</h3>
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-sm py-0.5 px-3">
                $25/month
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Everything in verification, plus premium promotion across the entire directory.
            </p>
            <ul className="space-y-2 text-sm">
              {[
                'Pinned to the top of all search results and list views',
                'Highlighted gold card that stands out from other listings',
                'Sponsored badge visible on your card, detail panel, and merchant page',
                'Includes verification badge at no extra cost',
                'Priority support for listing updates',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card>
            <CardContent className="py-4 px-4 text-center">
              <div className="text-2xl font-bold text-bch">$10</div>
              <div className="text-xs text-muted-foreground mb-2">one-time</div>
              <div className="text-sm font-medium">Verified</div>
              <p className="text-xs text-muted-foreground mt-1">Trust badge + confirmed listing</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 dark:border-amber-800/40">
            <CardContent className="py-4 px-4 text-center">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">$25</div>
              <div className="text-xs text-muted-foreground mb-2">per month</div>
              <div className="text-sm font-medium">Sponsored</div>
              <p className="text-xs text-muted-foreground mt-1">Top placement + gold highlight</p>
            </CardContent>
          </Card>
        </div>

        <Separator />

        {/* CTA */}
        <Card className="bg-bch/5 border-bch/20">
          <CardContent className="py-8 px-6 text-center space-y-4">
            <div className="inline-flex items-center justify-center size-12 rounded-full bg-bch/10 mx-auto">
              <MessageCircle className="size-6 text-bch" />
            </div>
            <h2 className="text-xl font-bold">Ready to get started?</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Verify your business yourself in a couple of minutes. For sponsored listings or any
              questions, reach out to BCH Reply Guy on Nostr.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button className="bg-bch hover:bg-bch/90 text-bch-foreground" onClick={() => setVerifyOpen(true)}>
                <ShieldCheck className="size-4 mr-2" />
                Get Verified on BCHNostr
              </Button>
              <Button asChild variant="outline">
                <a href={NOSTR_PROFILE_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="size-4 mr-2" />
                  Contact BCH Reply Guy
                </a>
              </Button>
            </div>
            <div className="pt-2">
              <Badge variant="outline" className="text-xs font-mono">
                {ADMIN_NPUB.slice(0, 20)}...{ADMIN_NPUB.slice(-8)}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Clock className="size-3" />
              Typical response time: under 24 hours
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="container px-4 mx-auto max-w-3xl py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-5 rounded bg-bch">
                <MapPin className="size-3 text-bch-foreground" />
              </div>
              <Link to="/" className="hover:text-foreground transition-colors">BCH Local</Link>
              <span className="text-border">|</span>
              <span>Powered by Nostr</span>
            </div>
            <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </footer>

      {/* Self-service BCHNostr verification dialog */}
      <BchnostrVerifyDialog open={verifyOpen} onOpenChange={setVerifyOpen} />
    </div>
  );
}
